import { Avatar, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLazyGetMyFollowersPageQuery } from '../../api/users'
import type { TUserListItem, TUsersListResponse } from '../../api/types/users'
import { routesProfile } from '../../consts'
import { CURSOR_LIST_PAGE_SIZE } from '../../consts/api'
import modalEmitter from '../ModalComponent/modalEmitter'

function getInitials(name: string | null | undefined): string {
  const value = (name ?? '').trim()
  if (!value) return '?'
  const parts = value.split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? ''
  const second = (parts[1]?.[0] ?? parts[0]?.[1] ?? '') ?? ''
  return (first + second).toUpperCase()
}

function displayUserName(u: TUserListItem): string {
  const name = (u.fullname ?? u.shortname ?? '').trim()
  return name || 'Пользователь'
}

function mergeUsersById(base: TUsersListResponse, next: TUsersListResponse): TUsersListResponse {
  const ids = new Set(base.map((item) => item.id))
  const appended = next.filter((item) => !ids.has(item.id))
  return [...base, ...appended]
}

export const UserFollowersModalBody = () => {
  const navigate = useNavigate()
  const [items, setItems] = useState<TUsersListResponse>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [requestedCursor, setRequestedCursor] = useState<string | null>(null)
  const [loadPage, { data, isLoading, isFetching, isError }] = useLazyGetMyFollowersPageQuery()

  const requestPage = useCallback(
    (cursor: string | null) => {
      setRequestedCursor(cursor)
      loadPage({ limit: CURSOR_LIST_PAGE_SIZE, cursor: cursor ?? undefined }, true)
    },
    [loadPage]
  )

  useEffect(() => {
    setItems([])
    setNextCursor(null)
    requestPage(null)
  }, [requestPage])

  useEffect(() => {
    if (!data) {
      return
    }

    const nextItems = data.items ?? []
    setNextCursor(data.meta?.next_cursor ?? null)
    setItems((prev) =>
      requestedCursor ? mergeUsersById(prev, nextItems) : mergeUsersById([], nextItems)
    )
  }, [data, requestedCursor])

  const canLoadMore = Boolean(nextCursor) && !isFetching

  const handleOpenProfile = (user: TUserListItem) => {
    const slug = user.slug?.trim()
    if (!slug) {
      return
    }
    modalEmitter.emitCloseModal()
    navigate(`/${routesProfile.USERS.path}/${slug}`)
  }

  return (
    <Stack gap={2}>
      {isLoading && (
        <Stack direction="row" gap={2} alignItems="center">
          <CircularProgress size={20} />
          <Typography>Загружаем подписчиков…</Typography>
        </Stack>
      )}

      {isError && (
        <Typography color="error">
          Не удалось загрузить список подписчиков.
        </Typography>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <Typography>Пока нет подписчиков.</Typography>
      )}

      {items.map((user) => {
        const title = displayUserName(user)
        const canNavigate = Boolean(user.slug?.trim())

        return (
          <Stack
            key={user.id}
            direction="row"
            gap={2}
            alignItems="center"
            onClick={canNavigate ? () => handleOpenProfile(user) : undefined}
            sx={{ cursor: canNavigate ? 'pointer' : 'default' }}
          >
            <Avatar src={user.avatar ?? undefined} alt={title}>
              {getInitials(title)}
            </Avatar>
            <Typography>{title}</Typography>
          </Stack>
        )
      })}

      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="follow"
          disabled={!canLoadMore}
          onClick={() => requestPage(nextCursor)}
        >
          {isFetching ? 'Загрузка…' : 'Показать ещё'}
        </Button>
      </Stack>
    </Stack>
  )
}
